import { useEffect, useState } from "react";
import type { LocalDeviceAccount } from "../data/round-journal.js";
import { deviceAccountCache, roundJournal } from "../data/local-database.js";

export interface DeviceRecovery {
  account: LocalDeviceAccount;
  hasUnsynced: boolean;
  hasRound: boolean;
}

export function useDeviceRecovery(sessionAccountId?: string): {
  loading: boolean;
  recovery: DeviceRecovery | null;
} {
  const [state, setState] = useState<{
    accountId: string | undefined;
    loading: boolean;
    recovery: DeviceRecovery | null;
  }>({ accountId: sessionAccountId, loading: true, recovery: null });

  useEffect(() => {
    let active = true;
    void loadDeviceRecovery(sessionAccountId)
      .then((recovery) => {
        if (active)
          setState({ accountId: sessionAccountId, loading: false, recovery });
      })
      .catch(() => {
        if (active)
          setState({
            accountId: sessionAccountId,
            loading: false,
            recovery: null,
          });
      });
    return () => {
      active = false;
    };
  }, [sessionAccountId]);

  return state.accountId === sessionAccountId
    ? state
    : { loading: true, recovery: null };
}

export async function loadDeviceRecovery(
  sessionAccountId?: string,
): Promise<DeviceRecovery | null> {
  const recoveries = await Promise.all(
    (await deviceAccountCache.listRecent()).map(async (account) => ({
      account,
      hasUnsynced: await roundJournal.hasUnsynced(account.accountId),
      hasRound: Boolean(
        await roundJournal.getLatestRecoverableSnapshot(account.accountId),
      ),
    })),
  );
  const available = recoveries.filter(
    (item) => item.hasUnsynced || item.hasRound,
  );
  return (
    available.find((item) => item.account.accountId !== sessionAccountId) ??
    available[0] ??
    null
  );
}
